declare global {
  interface Window {
    // events.js reads this to locate the global queue — must be set or it throws.
    TiktokAnalyticsObject?: string
    ttq?: {
      push: (args: unknown[]) => number
      methods: string[]
      setAndDefer: (target: any, method: string) => void
      load: (pixelId: string) => void
      page: (...args: unknown[]) => void
      track: (event: string, properties?: Record<string, unknown>, options?: Record<string, unknown>) => void
      instance: (pixelId: string) => unknown[]
      _i?: Record<string, unknown[]>
      _t?: Record<string, number>
      _o?: Record<string, unknown>
    }
  }
}

// Ported from TikTok's official pixel base code (https://ads.tiktok.com), typed
// and split out instead of injecting a raw eval'd snippet string. `ttq` starts
// as a plain array — its queued `[method, ...args]` tuples get replayed once
// the real events.js script loads and takes over `window.ttq`.
//
// `load` is deliberately NOT one of the auto-deferred `methods` — TikTok defines
// it as its own function that injects the real events.js script (below), so a
// naive `methods.forEach(setAndDefer)` never assigns it and `ttq.load(...)`
// throws "not a function".
function injectPixel(pixelId: string) {
  if (window.ttq)
    return

  // TikTok's events.js reads `window.TiktokAnalyticsObject` to find the global
  // queue. Omitting it (as the earlier port did) makes events.js throw "Cannot
  // set properties of undefined (setting '_env')", so it never takes over
  // `window.ttq` and no browser events are ever sent — the queued `[method,...]`
  // tuples just pile up. Server-side CAPI still works, but the client pixel is dead.
  window.TiktokAnalyticsObject = 'ttq'

  const ttq = [] as unknown as NonNullable<Window['ttq']>
  ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent', 'revokeConsent', 'grantConsent']
  ttq.setAndDefer = (target: any, method: string) => {
    target[method] = (...args: unknown[]) => target.push([method, ...args])
  }
  ttq.methods.forEach(method => ttq.setAndDefer(ttq, method))

  // Part of TikTok's official base code; events.js expects it to exist.
  ttq.instance = (id: string) => {
    const instance = ttq._i?.[id] || []
    ttq.methods.forEach(method => ttq.setAndDefer(instance, method))
    return instance
  }

  ttq.load = (id: string) => {
    const baseUrl = 'https://analytics.tiktok.com/i18n/pixel/events.js'
    ttq._i = ttq._i || {}
    ttq._i[id] = []
    // events.js reads `_u` (the base script url) off the per-id instance array.
    ;(ttq._i[id] as any)._u = baseUrl
    ttq._t = ttq._t || {}
    ttq._t[id] = Date.now()
    ttq._o = ttq._o || {}
    ttq._o[id] = {}

    const script = document.createElement('script')
    script.async = true
    script.src = `${baseUrl}?sdkid=${id}&lib=ttq`
    const firstScript = document.getElementsByTagName('script')[0]
    firstScript?.parentNode?.insertBefore(script, firstScript)
  }

  window.ttq = ttq
  window.ttq.load(pixelId)
  window.ttq.page()
}

export default defineNuxtPlugin((nuxtApp) => {
  // `tiktokPixel` is only declared in apps that opt in (see app/config/runtime.ts),
  // so it's untyped for every other app extending this layer — cast like the Meta Pixel plugin.
  const config = useRuntimeConfig() as any
  // Nitro's runtime env override runs values through `destr`; coerce to string
  // in case a numeric-looking id arrives as a number.
  const pixelId: string | number | undefined = config.public?.tiktokPixelId
  if (!pixelId)
    return

  nuxtApp.hook('app:mounted', () => {
    injectPixel(String(pixelId))

    const router = useRouter()
    router.afterEach(() => nextTick(() => window.ttq?.page()))
  })
})
